import React from "react";
import { useNavigate } from "react-router-dom";
import { Compass, MessagesSquare, HeartHandshake, Sparkles, ShieldCheck, Leaf } from "lucide-react";
import { Logo, Button } from "@/components/kit";
import { Reveal } from "@/components/Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1740867781711-36cfc791cb14?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const STEPS = [
  { n: "01", icon: Compass, title: "Explore", text: "Discover people who share your interests, values, and relationship intentions." },
  { n: "02", icon: MessagesSquare, title: "Connect", text: "Start genuine conversations and get to know someone naturally." },
  { n: "03", icon: HeartHandshake, title: "Focus", text: "When you’re ready, choose to focus on one meaningful connection together." },
];

const VALUES = [
  { icon: Leaf, title: "Intentional Discovery", text: "Discover thoughtfully, without endless swiping." },
  { icon: Sparkles, title: "Meaningful Connections", text: "Build connections through genuine conversations." },
  { icon: MessagesSquare, title: "Respectful Communication", text: "Communicate clearly, respect boundaries, and move forward with confidence." },
];

export default function WelcomePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F8F6F1] text-[#1F2B26] antialiased">
      {/* Nav */}
      <header className="sticky top-0 z-50 bg-[#F8F6F1]/85 backdrop-blur-md border-b border-[#E3DCD0]">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 h-16 flex items-center justify-between">
          <Logo variant="horizontal" className="h-8 sm:h-9" />
          <Button variant="ghost" size="sm" data-testid="welcome-nav-signin-btn" onClick={() => navigate("/login")}>
            Sign in
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-6xl px-5 sm:px-8 pt-10 sm:pt-16 pb-14 lg:pt-24 lg:pb-24">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <Reveal>
            <p className="font-serif italic text-xl text-[#8B9D90]">Be Sincere.</p>
            <h1 className="font-serif text-[2.5rem] leading-[1.08] sm:text-5xl lg:text-6xl text-[#1F2B26] mt-3">
              Something meaningful starts with sincerity.
            </h1>
            <p className="font-sans text-base sm:text-lg text-[#5F6D66] leading-relaxed mt-5 max-w-xl">
              Meet people who value genuine connections, clear intentions, and meaningful relationships.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <Button size="lg" data-testid="welcome-create-account-btn" onClick={() => navigate("/register")}>
                Get started
              </Button>
              <Button variant="outline" size="lg" data-testid="welcome-sign-in-btn" onClick={() => navigate("/login")}>
                I already have an account
              </Button>
            </div>
            <div className="flex items-center gap-2 text-[#8B9D90] mt-6">
              <ShieldCheck className="h-4 w-4" strokeWidth={1.6} />
              <span className="font-sans text-xs tracking-wide">For verified adults, 18 and over</span>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="relative">
              <div className="overflow-hidden rounded-[1.75rem] border border-[#E3DCD0] shadow-[0_24px_70px_rgba(37,59,53,0.14)] aspect-[4/3] lg:aspect-[4/5]">
                <img src={HERO_IMG} alt="Warm, calm daylight through a window" className="h-full w-full object-cover" loading="eager" />
              </div>
              <div className="hidden sm:flex absolute -bottom-5 -left-4 items-center gap-3 rounded-2xl bg-[#F8F6F1] border border-[#E3DCD0] px-4 py-3 shadow-lg">
                <Logo variant="emblem" className="h-7" />
                <span className="font-serif text-lg text-[#253B35]">sincerr.</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* What is Sincerr */}
      <section className="border-y border-[#E3DCD0] bg-[#F1EEE7]">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 py-16 lg:py-24 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#1F2B26]">Dating, with intention.</h2>
            <p className="font-sans text-base sm:text-lg text-[#5F6D66] leading-relaxed mt-6">
              Sincerr is a modern relationship platform for people who are genuinely looking for something meaningful.
              No endless swiping. No unnecessary pressure. Just a more thoughtful way to discover people, build
              connections, and explore relationships at your own pace.
            </p>
          </Reveal>
        </div>
      </section>

      {/* How Sincerr works */}
      <section className="mx-auto max-w-6xl px-5 sm:px-8 py-16 lg:py-24">
        <Reveal>
          <h2 className="font-serif text-3xl sm:text-4xl text-[#1F2B26] text-center">How Sincerr works</h2>
        </Reveal>
        <div className="grid sm:grid-cols-3 gap-6 lg:gap-8 mt-12">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 100}>
              <div className="h-full rounded-2xl bg-white border border-[#E3DCD0] p-7">
                <div className="flex items-center justify-between">
                  <span className="font-serif text-3xl text-[#D8C5A7]">{s.n}</span>
                  <div className="h-11 w-11 rounded-full bg-[#F1EEE7] flex items-center justify-center">
                    <s.icon className="h-5 w-5 text-[#253B35]" strokeWidth={1.6} />
                  </div>
                </div>
                <h3 className="font-serif text-2xl text-[#1F2B26] mt-6">{s.title}</h3>
                <p className="font-sans text-sm text-[#5F6D66] leading-relaxed mt-2">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={120}>
          <p className="font-sans text-sm text-[#8A9790] text-center mt-8 max-w-xl mx-auto">
            Focus Mode is entirely optional and only begins when both people mutually agree.
          </p>
        </Reveal>
      </section>

      {/* What makes Sincerr different */}
      <section className="bg-[#253B35] text-[#F8F6F1]">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 py-16 lg:py-24">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl text-center">What makes Sincerr different?</h2>
          </Reveal>
          <div className="grid sm:grid-cols-3 gap-10 mt-14">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 100}>
                <div className="border-t border-[#8B9D90]/40 pt-6">
                  <v.icon className="h-6 w-6 text-[#CFAEAA]" strokeWidth={1.5} />
                  <h3 className="font-serif text-xl text-[#F8F6F1] mt-4">{v.title}</h3>
                  <p className="font-sans text-sm text-[#C7D1CA] leading-relaxed mt-2">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Closing */}
      <section className="bg-[#F3EBE1]">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 py-20 lg:py-28 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1F2B26]">Ready for something meaningful?</h2>
            <p className="font-sans text-base sm:text-lg text-[#5F6D66] mt-5">
              Your next connection begins with being yourself.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-9">
              <Button size="lg" data-testid="welcome-join-btn" onClick={() => navigate("/register")}>
                Join Sincerr
              </Button>
              <Button variant="outline" size="lg" data-testid="welcome-signin-bottom-btn" onClick={() => navigate("/login")}>
                Sign in
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#F8F6F1] border-t border-[#E3DCD0]">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-5">
          <Logo variant="horizontal" className="h-8" />
          <div className="flex items-center gap-6">
            <button className="font-sans text-sm text-[#5F6D66] hover:text-[#253B35] underline underline-offset-2" data-testid="welcome-terms-link">
              Terms of Service
            </button>
            <button className="font-sans text-sm text-[#5F6D66] hover:text-[#253B35] underline underline-offset-2" data-testid="welcome-privacy-link">
              Privacy Policy
            </button>
          </div>
          <p className="font-sans text-xs text-[#B2BAB4]">© {new Date().getFullYear()} Sincerr · Be Sincere.</p>
        </div>
      </footer>
    </div>
  );
}
